import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { NodeEventPlugin } from "@lexical/react/LexicalNodeEventPlugin";
import {EditorRefPlugin} from "@lexical/react/LexicalEditorRefPlugin";
import {ClearEditorPlugin} from "@lexical/react/LexicalClearEditorPlugin";
import {
  $getNodeByKeyOrThrow,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  DOMConversionMap,
  DOMExportOutput,
  DOMExportOutputMap,
  isHTMLElement,
  KEY_DOWN_COMMAND,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from 'lexical';

import {forwardRef, useEffect, useImperativeHandle, useRef, useState, type ForwardedRef} from "react";

import {
  HeadingNode
} from '@lexical/rich-text'

import ToolbarPlugin, { EDIT_EQUATION_COMMAND } from './plugins/ToolbarPlugin.tsx';
import EquationPlugin from './plugins/EquationPlugin.tsx';
import Theme from './Theme.ts';
import TreeViewPlugin from './plugins/TreeViewPlugin.tsx';
import { EquationNode } from './nodes/EquationNode.tsx';
import StatePlugin from './plugins/StatePlugin.tsx';
import { lineHeights } from './plugins/ToolbarContext.tsx';
import { $createCustomParagraphNode, CustomParagraphNode } from './nodes/CustomParagraph.ts';
import { rgbToStr, parseRgb } from './components/ColorPicker.tsx';
import { $generateHtmlFromNodes } from '@lexical/html';

const placeholder = 'Enter text...';

const removeClassExportDOM = (
  editor: LexicalEditor,
  target: LexicalNode,
): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    // Remove all inline styles and classes if the element is an HTMLElement
    // Children are checked as well since TextNode can be nested
    // in i, b, and strong tags.
    for (const el of [
      output.element,
      ...output.element.querySelectorAll('[class]'),
    ]) {
      el.removeAttribute('class');
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [CustomParagraphNode, removeClassExportDOM],
  [TextNode, removeClassExportDOM],
  [HeadingNode, removeClassExportDOM],
]);

export function parseAllowedColor(input: string) {
  try {
    let rgb = parseRgb(input)
    return rgbToStr(rgb)
  } catch {
    return '';
  }
}

export function parseAllowedLineHeight(input: string) {
  return lineHeights.includes(input) ? input : '';
}

function getExtraStyles(element: HTMLElement): string {
  let extraStyles = '';
  const color = parseAllowedColor(element.style.color);
  const lineHeight = parseAllowedLineHeight(element.style.lineHeight);
  if (color !== '' && color !== 'rgb(0, 0, 0)') {
    extraStyles += `color: ${color};`;
  }
  if (lineHeight !== '') {
    extraStyles += `line-height: ${lineHeight};`;
  }
  return extraStyles;
}

function buildImportMap(): DOMConversionMap {
  const importMap: DOMConversionMap = {};

  // Wrap all TextNode importers with a function that also imports
  // the custom styles implemented by the playground
  for (const [tag, fn] of Object.entries(TextNode.importDOM() || {})) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }
          const extraStyles = getExtraStyles(element);
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  }

  return importMap;
}

const editorConfig = {
  html: {
    export: exportMap,
    import: buildImportMap(),
  },
  namespace: 'React.js Demo',
  nodes: [ParagraphNode, CustomParagraphNode, TextNode, HeadingNode, EquationNode, {
    replace: ParagraphNode,
    with: (node: ParagraphNode) => {
      return $createCustomParagraphNode();
    },
    withKlass: CustomParagraphNode,
  }],
  onError(error: Error) {
    throw error;
  },
  theme: Theme
};

type Params = {
  onChange: (content: string) => void,
  onAutosave: (content: string) => void,
  type: 'none' | 'basic',
  showEquationEditorButton: boolean,
  equationEditorContainerElement?: HTMLElement,
  initialData?: string,
  id: string,
}
export default forwardRef((props: Params, handleRef: ForwardedRef<{getCurrentContent: () => string | undefined}>) => {
  const editorRef = useRef(null as LexicalEditor | null);

  useImperativeHandle(handleRef, () => ({
    getCurrentContent: () => {
      let currentContent: string | undefined = undefined;
      if (editorRef.current) {
        return editorRef.current.read(() => {
          return $generateHtmlFromNodes(editorRef.current!);
        })
      }
      return currentContent;
    }
  }));

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="editor-container">
        <ToolbarPlugin type={props.type} showEquationEditorButton={props.showEquationEditorButton} equationEditorContainerElement={props.equationEditorContainerElement} />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                aria-placeholder={placeholder}
                placeholder={
                  <div className="editor-placeholder">{placeholder}</div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <StatePlugin id={props.id} onChange={props.onChange} autosave={props.onAutosave} html={props.initialData} />
          <EquationPlugin />
          <NodeEventPlugin
            nodeType={EquationNode}
            eventType={'dblclick'}
            eventListener={(e: Event, editor, nodeKey) => {
              let equationNode = $getNodeByKeyOrThrow<EquationNode>(nodeKey)
              editor.dispatchCommand(
                EDIT_EQUATION_COMMAND,
                equationNode
              )
            }}
          />
          <ClearEditorPlugin/>
          <EditorRefPlugin editorRef={editorRef}/>
          {/* <TabIndentationPlugin maxIndent={7}/> */}
          <TreeViewPlugin/>
        </div>
      </div>
    </LexicalComposer>
  );
});